// Security utilities for error handling and validation
export const sanitizeErrorMessage = (error: Error | unknown): string => {
  if (process.env.NODE_ENV === 'development') {
    return error instanceof Error ? error.message : String(error);
  }
  
  // In production, return generic error messages to avoid info leakage
  if (error instanceof Error) {
    // Allow specific safe error types
    if (error.name === 'ValidationError' || error.name === 'NotFoundError') {
      return error.message;
    }
  }
  
  return 'An unexpected error occurred. Please try again.';
};

export const safeWindowAccess = <T>(
  callback: () => T,
  fallback: T
): T => {
  try {
    if (typeof window !== 'undefined') {
      return callback();
    }
    return fallback;
  } catch (error) {
    console.warn('Safe window access failed:', error);
    return fallback;
  }
};

export const safeDocumentAccess = <T>(
  callback: () => T,
  fallback: T
): T => {
  try {
    if (typeof document !== 'undefined') {
      return callback();
    }
    return fallback;
  } catch (error) {
    console.warn('Safe document access failed:', error);
    return fallback;
  }
};

// Rate limiting utility for client-side form submissions
class RateLimiter {
  private attempts: Map<string, number[]> = new Map();
  
  isAllowed(key: string, maxAttempts: number = 5, windowMs: number = 60000): boolean {
    const now = Date.now();
    const attempts = this.attempts.get(key) || [];
    
    // Remove old attempts outside the time window
    const recentAttempts = attempts.filter(time => now - time < windowMs);
    
    if (recentAttempts.length >= maxAttempts) {
      return false;
    }
    
    // Add current attempt
    recentAttempts.push(now);
    this.attempts.set(key, recentAttempts);
    
    return true;
  }
  
  reset(key: string): void {
    this.attempts.delete(key);
  }
}

export const rateLimiter = new RateLimiter();

export const getClientIdentifier = (): string => {
  return safeWindowAccess(
    () => {
      // Create a semi-unique identifier for rate limiting
      const nav = navigator;
      return btoa(
        `${nav.userAgent}-${window.location.hostname}-${nav.language}`
      ).slice(0, 16);
    },
    'default-client'
  );
};